import React, { useMemo, useState } from 'react';
import { buildPartGroups, SX } from './cannabisLoaderGeometry';
import { haloRevealed, isPathRevealed, statusLabelForProgress } from './cannabisLoaderPathThresholds';
import styles from './CannabisLoader.module.css';

/**
 * Shefa Green premium line-art loader. Drive with `progress` 0–100.
 * @param {object} props
 * @param {number} [props.progress=0]
 * @param {boolean} [props.fullScreen=true]
 * @param {boolean} [props.debug=false] — range slider; ignores `progress` when true
 * @param {string} [props.className]
 */
export default function CannabisLoader({
  progress: progressProp = 0,
  fullScreen = true,
  debug = false,
  className = '',
}) {
  const [debugVal, setDebugVal] = useState(0);
  const progress = debug ? debugVal : Math.max(0, Math.min(100, progressProp));

  const partGroups = useMemo(() => buildPartGroups(), []);
  const logoVisible = progress > 5;
  const statusText = statusLabelForProgress(progress);
  const showHalo = haloRevealed(progress);

  return (
    <div
      className={`${styles.root} ${fullScreen ? styles.rootFullscreen : ''} ${className}`.trim()}
      role="status"
      aria-live="polite"
      aria-busy
    >
      <svg
        className={styles.plantSvg}
        viewBox="0 0 160 192"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        {partGroups.map((group) =>
          group.paths.map((path) => {
            const isDrawn = isPathRevealed(path.id, progress);
            return (
              <path
                key={path.id}
                d={path.d}
                strokeWidth={path.strokeWidth}
                className={`${styles.part} ${isDrawn ? styles.partDraw : ''}`}
                style={
                  isDrawn
                    ? {
                        ['--L']: `${path.L}`,
                        ['--t']: path.t,
                      }
                    : {
                        ['--L']: `${path.L}`,
                        ['--t']: path.t,
                      }
                }
              />
            );
          })
        )}

        <g className={`${styles.haloGroup} ${showHalo ? styles.haloGroupVisible : ''} ${styles.part}`}>
          <g className={styles.haloSpinner}>
            <circle
              cx={SX}
              cy={22}
              r={15}
              fill="none"
              stroke="#1a331a"
              strokeWidth="0.3"
              strokeDasharray="2 5"
            />
          </g>
        </g>
      </svg>

      <div className={styles.footer}>
        <div className={`${styles.logoText} ${logoVisible ? styles.logoTextVisible : ''}`}>
          Shefa Green
        </div>
        <span className={styles.statusText}>{statusText}</span>
        <div className={styles.progressTrack}>
          <div className={styles.progressFill} style={{ width: `${progress}%` }} />
        </div>
      </div>

      {debug ? (
        <label className={styles.debug}>
          Debug progress: {debugVal}
          <input
            type="range"
            min={0}
            max={100}
            value={debugVal}
            onChange={(e) => setDebugVal(Number(e.target.value))}
          />
        </label>
      ) : null}
    </div>
  );
}
